import './heroIntro.scss';
import {gsap} from 'gsap';

// Matches docs/gsap-animation-spec.md section 1-2.
// Sequence:
//   0. Hold — big centered wordmark, full black, nothing else.
//   1. Logo translates LEFT ONLY (same big size) to its final horizontal (X)
//      resting spot — which is already the exact X the small nav logo will
//      occupy. A line grows from the viewport's RIGHT edge leftward at the
//      same time, stopping short of the logo (a visible gap). Logo and line
//      share the exact same duration/ease here on purpose: two independently
//      eased tweens can each land on the correct FINAL gap and still cross
//      paths mid-flight (different rate curves), which read as the line
//      "catching" the logo for a moment.
//   2. Logo SCALES DOWN + moves vertically into the header's logo slot (X
//      unchanged). The line keeps growing further left, reaching the full
//      left edge (edge-to-edge) by the end of this phase. Crossfade to the
//      real header logo/menu happens at the very end.
//   3. Brief hold — full-width line, nothing else.
//   4. The line splits at its own center into two halves; each shrinks
//      toward its own outer edge, opening a gap that grows from the center
//      outward. The curtain behind that gap was already dropped (instantly,
//      hidden under the line) the moment the line finished growing in phase
//      2, so clearing the line reveals actual hero content immediately, not
//      more black.
//   5. The remaining black area shrinks away vertically in ONE piece per
//      side: a single top curtain (spanning the full height from the
//      viewport's top edge — including the header strip — down to the gap)
//      shrinks upward, and the bottom curtain shrinks downward, until the
//      hero is fully revealed. The top curtain is ONE element/ONE tween —
//      not a curtain-plus-a-separate-header-piece — so there is no seam or
//      visible division anywhere in the top black mass.
function buildOverlay() {
  const root = document.createElement('div');
  root.className = 'hero-intro';
  root.setAttribute('aria-hidden', 'true');
  root.innerHTML = `
    <div class="hero-intro__curtain hero-intro__curtain--below"></div>
    <span class="hero-intro__line"></span>
    <span class="hero-intro__line-half hero-intro__line-half--left"></span>
    <span class="hero-intro__line-half hero-intro__line-half--right"></span>
    <img class="hero-intro__logo" alt="" />
  `;
  document.body.appendChild(root);

  // The top curtain is a SINGLE element covering the full height from the
  // viewport's top edge (through the header) down to the line's gap. It
  // lives OUTSIDE `.hero-intro` (that wrapper is z-index 2000, ABOVE the
  // real header's z-index 1000) at its own z-index just below the header, so
  // the header's own — theme-dependent, often transparent — background lets
  // this one curtain's black show through for the header-strip portion of
  // it, while the header's real content (logo/menu, both opaque) stays
  // visible on top of it the whole time. A child of `.hero-intro` couldn't
  // do this: nested inside a stacking context already pinned at 2000, it
  // would always paint above the header no matter what z-index it was
  // given — which is why an earlier version of this file wrongly split the
  // header strip into a second, separately-animated curtain element instead
  // of just placing ONE curtain at the right z-index.
  const topCurtain = document.createElement('div');
  topCurtain.className = 'hero-intro__top-curtain';
  topCurtain.setAttribute('aria-hidden', 'true');
  document.body.appendChild(topCurtain);

  return {
    root,
    topCurtain,
    curtainBelow: root.querySelector('.hero-intro__curtain--below'),
    line: root.querySelector('.hero-intro__line'),
    lineLeftHalf: root.querySelector('.hero-intro__line-half--left'),
    lineRightHalf: root.querySelector('.hero-intro__line-half--right'),
    logo: root.querySelector('.hero-intro__logo'),
  };
}

export function heroIntro() {
  const heroBlock = document.querySelector('[data-component="home-hero"]');
  if (!heroBlock || heroBlock.dataset.heroIntroInitialized === 'true') return;

  const header = document.querySelector('[data-site-header]');
  const realLogo = header ? header.querySelector('.rebredg-header-logo-image') : null;
  const menuToggle = header ? header.querySelector('.rebredg-menu-toggle') : null;

  if (!header || !realLogo) return;

  heroBlock.dataset.heroIntroInitialized = 'true';

  // A reload while scrolled mid-page would otherwise leave the browser's own
  // scroll restoration in place, so the intro plays off-screen above a hero
  // the visitor never sees. Force the page to the top before building the
  // overlay, and stop the browser from re-restoring the old position after.
  //
  // `behavior: 'instant'` is required, not the 2-argument `scrollTo(0, 0)`
  // form: the theme sets `html { scroll-behavior: smooth }` globally, so a
  // plain scrollTo animates toward 0 instead of jumping — and the very next
  // line locks scroll (`overflow: hidden` via lockInteraction()), which cuts
  // that in-flight animation off wherever it happened to be, leaving the
  // page frozen mid-scroll instead of at the top.
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }
  window.scrollTo({top: 0, left: 0, behavior: 'instant'});

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const lockInteraction = () => {
    document.documentElement.classList.add('hero-intro-locking');
    if (menuToggle) {
      menuToggle.setAttribute('aria-disabled', 'true');
      menuToggle.style.pointerEvents = 'none';
    }
  };

  const unlockInteraction = () => {
    document.documentElement.classList.remove('hero-intro-locking');
    if (menuToggle) {
      menuToggle.removeAttribute('aria-disabled');
      menuToggle.style.pointerEvents = '';
    }
  };

  const {root, topCurtain, curtainBelow, line, lineLeftHalf, lineRightHalf, logo} = buildOverlay();
  logo.src = realLogo.currentSrc || realLogo.src;

  lockInteraction();
  gsap.set([realLogo, menuToggle], {autoAlpha: 0});

  const cleanup = () => {
    gsap.set(
        [topCurtain, curtainBelow, line, lineLeftHalf, lineRightHalf, logo, realLogo, menuToggle],
        {clearProps: 'all'},
    );
    topCurtain.remove();
    root.remove();
    unlockInteraction();
  };

  if (reducedMotion) {
    cleanup();
    return;
  }

  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  // Matches the CSS breakpoint in heroIntro.scss.
  const lineThickness = viewportW <= 768 ? 8 : 16;

  // Big centered wordmark — a fraction of the viewport width, comfortably
  // larger than the final nav logo (per the client: it must read as "large",
  // not the final size). Clamped so it stays sane on very small/large screens.
  const bigLogoWidth = Math.max(200, Math.min(viewportW * 0.34, 460));
  const naturalWidth = Number(realLogo.getAttribute('width')) || realLogo.naturalWidth || 175;
  const naturalHeight = Number(realLogo.getAttribute('height')) || realLogo.naturalHeight || 49;

  logo.style.width = `${bigLogoWidth}px`;
  logo.style.height = `${bigLogoWidth * (naturalHeight / naturalWidth)}px`;
  // Scale happens around the LEFT edge (not center) so phase 2's shrink
  // doesn't have to fight to keep the horizontal position phase 1 already
  // established — the left edge simply stays put as it scales down.
  gsap.set(logo, {transformOrigin: 'left center'});

  const bigLogoRect = logo.getBoundingClientRect();
  const realLogoRect = realLogo.getBoundingClientRect();

  const lineY = bigLogoRect.top + bigLogoRect.height / 2;
  // How far past the logo's phase-1 resting right edge the line stops — a
  // visible gap, per the reference (it never touches the logo). Generous
  // margin since phase 1 keeps logo and line perfectly in sync now (see
  // below), this is a fixed safety buffer, not something fighting easing
  // drift.
  const lineGap = Math.max(40, viewportW * 0.04);
  const logoPhase1Right = realLogoRect.left + bigLogoRect.width;
  const lineStopX = Math.min(viewportW, logoPhase1Right + lineGap);
  // scaleX is a fraction of the line's full (viewport-wide) span, anchored
  // right — 0 = invisible, 1 = full width. The phase-1 stop point expressed
  // as that same fraction:
  const linePhase1Scale = Math.max(0, (viewportW - lineStopX) / viewportW);

  // The strip where the line sits (gapTop..gapBottom, exactly the line's own
  // thickness, centered on lineY) starts OVERLAPPED by both curtains, not
  // left empty: if it were a real hole from the start, whatever the line's
  // still-growing width hasn't covered yet (its left portion, before phase 2
  // finishes) would already show the hero through, since the line is the
  // ONLY thing over that strip while it's mid-scaleX-growth. Both curtains
  // reach past lineY by half the line's thickness through phase 2. The
  // instant the line finishes growing to full width, the curtains drop that
  // overlap in one frame (a `.set`, not a `.to` — see phase 2 below) — always
  // hidden under the still-opaque line, so the strip only visibly becomes a
  // real gap once the line itself splits and clears (phase 4).
  const gapTop = lineY - lineThickness / 2;
  const gapBottom = lineY + lineThickness / 2;

  // topCurtain spans from the viewport's own top edge (y=0, through the
  // header) down to the gap — ONE rectangle, ONE tween, covering everything
  // that used to be two separate curtain elements.
  const topCurtainHeightCovered = gapBottom;
  const topCurtainHeightRevealed = gapTop;
  const curtainBelowHeightCovered = Math.max(viewportH - gapTop, 0);
  const curtainBelowHeightRevealed = Math.max(viewportH - gapBottom, 0);

  gsap.set(topCurtain, {top: 0, height: topCurtainHeightCovered, transformOrigin: 'top'});
  gsap.set(curtainBelow, {top: gapTop, height: curtainBelowHeightCovered, transformOrigin: 'bottom'});
  gsap.set(line, {top: gapTop, height: lineThickness, width: viewportW, scaleX: 0});
  gsap.set([lineLeftHalf, lineRightHalf], {top: gapTop, height: lineThickness, autoAlpha: 0, scaleX: 1});

  const baseX = gsap.getProperty(logo, 'x');
  const baseY = gsap.getProperty(logo, 'y');
  // Phase 1: pure horizontal move to the final X (left edge lands exactly on
  // the real logo's left edge) — same big size, same vertical position.
  const phase1X = baseX + (realLogoRect.left - bigLogoRect.left);
  // Phase 2: scale down to the real logo's size (scaling from the left edge,
  // so X doesn't need to move again) and slide vertically onto its center.
  const phase2Scale = realLogoRect.width / bigLogoRect.width;
  const phase2Y = baseY + ((realLogoRect.top + realLogoRect.height / 2) - (bigLogoRect.top + bigLogoRect.height / 2));

  const tl = gsap.timeline({onComplete: cleanup});

  // Phase 0: hold — nothing is scheduled before 0.5s, so the timeline simply
  // sits on the initial (big, centered, no line) state until then.

  // Phase 1: move left + line draws to its stop point, just short of the
  // logo. SAME duration + ease on both tweens so they move in lockstep and
  // never cross paths mid-flight, even though the line moves faster overall.
  const PHASE1_DURATION = 1.4;
  const PHASE1_EASE = 'power2.out';

  tl.to(logo, {x: phase1X, duration: PHASE1_DURATION, ease: PHASE1_EASE}, 0.5)
      .to(line, {scaleX: linePhase1Scale, duration: PHASE1_DURATION, ease: PHASE1_EASE}, 0.5)
      .to(menuToggle, {autoAlpha: 1, duration: 0.4}, 0.9);

  // Phase 2: shrink into the nav slot + line completes to full width.
  tl.addLabel('phase2', 0.5 + PHASE1_DURATION + 0.2)
      .to(logo, {scale: phase2Scale, y: phase2Y, duration: 1.0, ease: 'power2.inOut'}, 'phase2')
      .to(line, {scaleX: 1, duration: 1.1, ease: 'power2.inOut'}, 'phase2')
      .to(logo, {autoAlpha: 0, duration: 0.25}, 'phase2+=0.85')
      .to([realLogo, menuToggle], {autoAlpha: 1, duration: 0.3}, 'phase2+=0.85')
      // The instant the line hits full width, drop the curtains' overlap
      // over the gap strip in one frame (`.set`, not `.to`) — invisible,
      // since the fully-opaque white line still sits on top covering that
      // exact strip pixel-for-pixel. This is what makes the later split
      // (below) reveal real hero content immediately instead of racing an
      // animated curtain retreat and briefly showing black/transparent.
      .set(topCurtain, {height: topCurtainHeightRevealed}, 'phase2+=1.1')
      .set(curtainBelow, {top: gapBottom, height: curtainBelowHeightRevealed}, 'phase2+=1.1');

  // Phase 3: brief hold on the full-width line. The gap behind it is already
  // open (previous step) but hidden under the opaque line, so this looks
  // identical to before.
  tl.addLabel('phase3', 'phase2+=1.2')
      .to({}, {duration: 0.4}, 'phase3');

  // Phase 4: line splits at center, halves clear to their own edge. The
  // curtain underneath is already gone (phase 2's instant `.set`), so this
  // shrink is the ONLY motion left and reveals real hero content the moment
  // each point of the line clears it — nothing left to race. Ease IN (slow
  // start, fast finish) per client request, not the symmetric in-out used
  // for the draw-in phases above.
  tl.addLabel('split', 'phase3+=0.4')
      .set(line, {autoAlpha: 0}, 'split')
      .set([lineLeftHalf, lineRightHalf], {autoAlpha: 1}, 'split')
      .to(lineLeftHalf, {scaleX: 0, duration: 0.9, ease: 'power2.in'}, 'split')
      .to(lineRightHalf, {scaleX: 0, duration: 0.9, ease: 'power2.in'}, 'split');

  // Phase 5: the two black panels shrink away vertically, fully revealing
  // the hero — topCurtain (one piece, header included) shrinks upward,
  // curtainBelow shrinks downward. Ease IN (slow start, fast finish), per
  // client request. Duration halved (client: "double speed") from the
  // original 2.4s.
  tl.addLabel('reveal', 'split+=0.9')
      .to(topCurtain, {scaleY: 0, duration: 1.2, ease: 'power2.in'}, 'reveal')
      .to(curtainBelow, {scaleY: 0, duration: 1.2, ease: 'power2.in'}, 'reveal');
}
